import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Row, Col, Typography, Radio } from 'antd';
import { ProjectDto } from '@/core';
import { EpicGroupCard } from './EpicGroupCard';
import { EpicGroupTypesEnum } from './hooks';
import { useFilteredEpicGroupsByType } from './hooks/useFilteredEpicGroupsByType';
import { ErrorCard } from '@/components';
import { validateEpicGroupsTab } from './helpers';
import classes from './EpicGroupCardsList.module.scss';

type CardsListProps = {
  projectData: ProjectDto;
};

export const EpicGroupCardsList = ({ projectData }: CardsListProps) => {
  const { t } = useTranslation(undefined, { keyPrefix: 'projects.constructor' });
  const [editedCardsIds, setEditedCardsIds] = useState<string[]>([]);
  const [filterValue, setFilterValue] = useState(EpicGroupTypesEnum.All);
  const { filteredEpicGroups, getItemsQuantityByFilter } = useFilteredEpicGroupsByType(
    projectData.epicGroups,
    filterValue,
  );
  const isEpicGroupsTabValid = validateEpicGroupsTab(projectData);

  const handleClickEdit = (id: string) => setEditedCardsIds((prev) => [...prev, id]);
  const handleStopEditCard = (id: string) =>
    setEditedCardsIds((prev) => prev.filter((editedCardId) => editedCardId !== id));

  return (
    <div className={classes.cardsList}>
      <Radio.Group buttonStyle="solid" value={filterValue} onChange={(e) => setFilterValue(e.target.value)}>
        <Radio.Button value={EpicGroupTypesEnum.All}>
          {t('addEpicGroups.all')}{' '}
          <span className={classes.quantityOfItems}>({projectData.epicGroups.length})</span>
        </Radio.Button>
        <Radio.Button value={EpicGroupTypesEnum.Products}>
          {t('addEpicGroups.types.product')}{' '}
          <span className={classes.quantityOfItems}>
            ({getItemsQuantityByFilter(EpicGroupTypesEnum.Products)})
          </span>
        </Radio.Button>
        <Radio.Button value={EpicGroupTypesEnum.CostTypes}>
          {t('addEpicGroups.types.cost')}{' '}
          <span className={classes.quantityOfItems}>
            ({getItemsQuantityByFilter(EpicGroupTypesEnum.CostTypes)})
          </span>
        </Radio.Button>
      </Radio.Group>
      {!isEpicGroupsTabValid && <ErrorCard text={t('addEpicGroups.invalidTab')} />}
      {filteredEpicGroups.length ? (
        <>
          <Row className={classes.cardsListTitle}>
            <Col xs={6} md={3}>
              <Typography.Text>{t('addEpicGroups.type')}</Typography.Text>
            </Col>
            <Col span={8}>
              <Typography.Text>{t('addEpicGroups.name')}</Typography.Text>
            </Col>
            <Col xs={10} md={13}>
              <Typography.Text>{t('addEpicGroups.epics')}</Typography.Text>
            </Col>
          </Row>
          {filteredEpicGroups.map((epicGroup) => (
            <EpicGroupCard
              projectData={projectData}
              key={epicGroup.id}
              onClickMenuEdit={() => handleClickEdit(epicGroup.id)}
              isEdit={editedCardsIds.includes(epicGroup.id)}
              stopEditCard={() => handleStopEditCard(epicGroup.id)}
              {...epicGroup}
            />
          ))}
        </>
      ) : null}
    </div>
  );
};
